import React from 'react'
import { ChartDataLabelsPlugin, ChartPlugin, DoughnutChartElement } from './chartJS'
import { ChartProps } from 'react-chartjs-2'
import { CHART_TOOLTIP_OPTIONS, ChartLabelColoring, ChartTitleWrapper } from './chartWrapper'

type Label = string
type Color = string
export type Slice = [Label, number, Color]
export type SunburstSlice = [Label, number, Color, Slice[]]

export type PieData = Slice[]
export type Legend = {
  side: 'top' | 'left'
  className?: string
}
export interface DonutChartProps {
  legend?: Legend
  title?: string
  data: PieData
  centerIcon?: React.ReactNode
  className?: string
  onLabelClick?: (labels: string[]) => void
  recreateOnDataChange?: boolean
  minDataFraction?: number
}

export const DonutChart: React.FC<DonutChartProps> = ({
  legend,
  title,
  data,
  centerIcon,
  className,
  onLabelClick,
  recreateOnDataChange = true,
  minDataFraction = 0.016,
}) => {
  const processedData = React.useMemo(() => {
    const total = data.reduce((a, b) => a + b[1], 0)
    return {
      labels: data.map((d) => d[0]),
      datasets: [
        {
          data: data.map((d) => Math.max(d[1], total * minDataFraction)),
          backgroundColor: data.map((d) => d[2]),
          borderWidth: 2,
        },
      ],
    }
  }, [data])

  const noData = !processedData.datasets.some((d) => d.data.some((e) => e > 0))

  const processedOptions = React.useMemo(() => {
    const options: ChartProps<'doughnut'>['options'] = {
      backgroundColor: 'transparent',
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          ...CHART_TOOLTIP_OPTIONS,
          callbacks: {
            label: function (this, tooltipItem) {
              // vals are changed to set a minimum slice size, retrieve the real value from the data
              const realVal = data[this.dataPoints[0].dataIndex][1]
              // return `${this.dataPoints[0].label}: ${realVal}`
              return `${realVal}`
            },
          },
        },
        datalabels: {
          color: ChartLabelColoring({
            tinyValueThreshold: 1 / 150,
          }),
          formatter: function (value, context) {
            // vals are changed to set a minimum slice size, retrieve the real value from the data
            const realVal = data[context.dataIndex][1]
            return realVal == 0 ? '' : '' + realVal
          },
        },
      },
      onClick: function (e, elements) {
        onLabelClick && onLabelClick(elements.map((element) => processedData.labels![element.index].toLowerCase()))
      },
    }
    return options
  }, [processedData, onLabelClick])

  const legendItems = React.useMemo(() => {
    if (!legend) return undefined
    return data.map((d) => ({
      label: d[0],
      color: d[2],
    }))
  }, [data, legend])

  // force recreate when data changes
  const chartKey = recreateOnDataChange ? React.useMemo(() => JSON.stringify(data), [data]) : ''

  return (
    <ChartTitleWrapper
      legendItems={legendItems}
      legend={legend}
      title={title}
      className={className}
      onLabelClick={onLabelClick}
      dummyLegend={noData}
    >
      <DoughnutChartElement
        key={chartKey}
        plugins={[EmptyPiePlugin, ChartDataLabelsPlugin]}
        data={processedData}
        options={processedOptions}
        className="z-10"
      />
      {centerIcon && <div className="absolute inset-auto">{centerIcon}</div>}
    </ChartTitleWrapper>
  )
}

export const EmptyPiePlugin: ChartPlugin<'doughnut'> = {
  id: 'noData',
  beforeInit: function (chart) {
    var data = chart.data.datasets
    var isAllZero = data.every((d) => d.data.every((e) => e === 0))
    if (!isAllZero) return
    // when all data values are zero...
    data.forEach((dataset, i) => {
      dataset.data = dataset.data.map((e, i) => (i > 0 ? 0 : 1)) //add one segment
      dataset.backgroundColor = '#e5e7eb' //change bg color to gray-200
      dataset.hoverBackgroundColor = '#e5e7eb' //change hover bg color to gray-200
      dataset.borderWidth = 0 //no border
      if (data.length > 1 && dataset.borderColor) {
        // probably a sunburst chart, make everything join
        dataset.borderColor = i > 0 ? 'j_inner' : 'j_upper'
      }
    })
    // turn off tooltip
    if (chart.options.plugins?.tooltip?.enabled) chart.options.plugins.tooltip.enabled = false
    // turn off data labels
    if (chart.options.plugins?.datalabels) chart.options.plugins.datalabels.display = false
    // turn off hover effect
    chart.options.hover = { mode: 'nearest', intersect: false }
    // remove onClick event
    chart.options.onClick = undefined
  },
}
